/**
 * Centralized API endpoint registry. Paths are relative to the axios
 * `baseURL` (`${VITE_API_URL}/api/v1` — see `api-client.ts`), so they
 * start after `/api/v1`. Dynamic segments are functions.
 *
 * This is the CUSTOMER portal — everything is under `/customer/*` and
 * scoped to the logged-in customer (session cookie `voxlane_customer_session`).
 */
export const API_ROUTES = {
  /**
   * Public branding — deliberately UNAUTHENTICATED, and doubly so here:
   * portal users are customers, not staff, so they could never hold an
   * admin permission. Anything gated would fail on exactly the screens
   * that need branding most.
   */
  BRANDING: {
    GET: "/branding",
    /** Square mark; 404 when `has_logo` is false, so don't request it then. */
    LOGO: "/branding/logo",
    /** Wide name-and-mark; 404 when `has_wordmark` is false. */
    WORDMARK: "/branding/wordmark",
  },

  AUTH: {
    LOGIN: "/customer/auth/login",
    LOGOUT: "/customer/auth/logout",
    SESSION: "/customer/auth/session",
    /** Second factor. `mfa_token` is single-use, 5-minute TTL. */
    LOGIN_MFA: "/customer/auth/login/mfa",
    /** Passwordless. Takes NO email — a response that varied by address
     *  would let anyone probe which accounts exist. */
    LOGIN_PASSKEY_START: "/customer/auth/login/passkey/start",
    LOGIN_PASSKEY_FINISH: "/customer/auth/login/passkey/finish",
  },

  /**
   * The signed-in customer's own account. Portal INFRASTRUCTURE, not a
   * module — reachable with zero modules granted, because someone must
   * always be able to secure their account.
   */
  SETTINGS: {
    PROFILE: "/customer/settings/profile",
    PASSWORD: "/customer/settings/password",
    EMAIL: "/customer/settings/email",
    SESSIONS: "/customer/settings/sessions",
    REVOKE_OTHER_SESSIONS: "/customer/settings/sessions/revoke-others",
    /** Sign out one session. Revoking the current one logs you out. */
    SESSION: (id: string) => `/customer/settings/sessions/${id}`,
    MFA: "/customer/settings/mfa",
    TOTP_START: "/customer/settings/mfa/totp/start",
    TOTP_CONFIRM: "/customer/settings/mfa/totp/confirm",
    TOTP_DISABLE: "/customer/settings/mfa/totp/disable",
    RECOVERY_CODES: "/customer/settings/mfa/recovery-codes",
    PASSKEYS_START: "/customer/settings/mfa/passkeys/start",
    PASSKEYS_FINISH: "/customer/settings/mfa/passkeys/finish",
    PASSKEY_DELETE: (id: string) => `/customer/settings/mfa/passkeys/${id}`,
  },

  /**
   * The utilities this customer is entitled to, with their nav. Returns
   * only modules that are both granted AND have a portal surface — an
   * empty array is a valid answer (account with nothing enabled yet).
   */
  MODULES: "/customer/modules",

  /**
   * The whole portal menu: `account` (platform pages every customer has,
   * e.g. Billing) plus the same `modules` list as above.
   */
  NAVIGATION: "/customer/navigation",

  /**
   * The sidebar's counts and flags in one small read (lanes needing
   * attention, lists by status, open invoices, …) — one key per module
   * the customer holds, plus `billing`.
   */
  NAVIGATION_BADGES: "/customer/navigation/badges",
  /** Home: what needs the customer and what's running, across apps. */
  HOME_BRIEFING: "/customer/home/briefing",

  /** Records the customer opened recently, per kind, current labels. */
  NAVIGATION_RECENTS: "/customer/navigation/recents",
  /** Record that a record was opened: `{kind, id}`, always 204. */
  NAVIGATION_VISITS: "/customer/navigation/visits",

  /**
   * The global ⌘K search across my lanes, Sift lists (names and the
   * numbers in them), calls and billing documents: `?q=&limit=`.
   */
  SEARCH: "/customer/search",

  /** Landing dashboard snapshot (`?range=24h|7d`). */
  DASHBOARD: "/customer/dashboard",

  /**
   * Sift — carrier scrub, scoped to the signed-in customer. Uploading only
   * estimates; ENRICH is the only endpoint that spends. PREVIEW/EXPORT run
   * against stored data, so they're free to fire as often as the UI likes.
   */
  SIFT: {
    LISTS: {
      /** Previews the CSV's COLUMNS (not a filter) for the column picker. */
      INSPECT: "/customer/sift/lists/inspect",
      LIST: "/customer/sift/lists",
      CREATE: "/customer/sift/lists",
      DETAIL: (id: string) => `/customer/sift/lists/${id}`,
      DELETE: (id: string) => `/customer/sift/lists/${id}`,
      /** What dipping this list costs now, and whether it's affordable. */
      QUOTE: (id: string) => `/customer/sift/lists/${id}/quote`,
      /**
       * ⚠ Spends money — charges the balance up front. Bare trigger; the
       * carrier was bound at create. 402 = balance too low, 409 = already
       * dipping or nothing left to dip.
       */
      ENRICH: (id: string) => `/customer/sift/lists/${id}/enrich`,
      /** SSE: `progress` events while the list is dipped; closes once settled. */
      EVENTS: (id: string) => `/customer/sift/lists/${id}/events`,
      /** Numbers on the list's locked carrier — paginated, no carrier param. */
      RESULTS: (id: string) => `/customer/sift/lists/${id}/results`,
      /** GET streaming CSV — locked to the list's carrier, no param. */
      EXPORT: (id: string) => `/customer/sift/lists/${id}/export`,
    },
    /**
     * Which carriers this customer may scrub for: `{ restricted, carriers }`.
     * Unrestricted = any carrier; restricted = exactly `carriers`.
     */
    CARRIER_ACCESS: "/customer/sift/carrier-access",
    CARRIERS: {
      /** Paginated + searchable browse for the carrier picker. */
      LIST: "/customer/sift/carriers",
      /** Typeahead for the same picker. */
      SUGGEST: "/customer/sift/carriers/suggest",
    },
    OVERVIEW: "/customer/sift/overview",
    /** The current price per dip, where no list is involved. */
    PRICING: "/customer/sift/pricing",
    /** Append `?days=` (1–90, default 7). */
    USAGE: "/customer/sift/usage",
  },

  VOXSENSE: {
    /** Routing state — sample %, dirty/applied, inbound IPs + forward pool. */
    ROUTING: "/customer/voxsense/routing",
    /** Push the current draft live (202 → job). */
    ROUTING_APPLY: "/customer/voxsense/routing/apply",
    /** Status of an apply job (queued → running → done | failed). */
    ROUTING_APPLY_STATUS: (jobId: string) =>
      `/customer/voxsense/routing/apply/${jobId}`,
    /** Inbound source IPs (who calls come from). */
    INBOUND: "/customer/voxsense/routing/inbound",
    INBOUND_DETAIL: (id: string) => `/customer/voxsense/routing/inbound/${id}`,
    /** Forward targets (round-robin pool calls go to). */
    OUTBOUND: "/customer/voxsense/routing/outbound",
    OUTBOUND_DETAIL: (id: string) =>
      `/customer/voxsense/routing/outbound/${id}`,
    /** Aggregated overview — `?range=24h|7d|30d&threshold=50..90`. */
    OVERVIEW: "/customer/voxsense/overview",
    /** Where to point carriers: signaling SIP endpoint(s) + sampling tier. */
    CONNECTION: "/customer/voxsense/connection",
    /** Your analysed calls — `?window=&q=&risk=&sentiment=&limit=&offset=`. */
    CALLS: "/customer/voxsense/calls",
    /** Streams a CSV of every matching call (same filters, ignores paging). */
    CALLS_EXPORT: "/customer/voxsense/calls/export",
    CALL_DETAIL: (id: string) => `/customer/voxsense/calls/${id}`,
    /** Streams the recording bytes (audio/wav) — fetch as a blob on demand. */
    CALL_RECORDING: (id: string) => `/customer/voxsense/calls/${id}/recording`,
  },

  BILLING: {
    /** Billing home: balance, renewal and per-utility spend. */
    OVERVIEW: "/customer/billing/overview",
    /** Prepaid wallet balance (where only the balance card is needed). */
    WALLET: "/customer/billing/wallet",
    /** Paginated ledger (`?module=&type=&skip=&limit=`). */
    TRANSACTIONS: "/customer/billing/transactions",
    /** IP-reissue fee + this customer's affordability for it. */
    REISSUE_FEE: "/customer/billing/reissue-fee",
    /**
     * Billing documents — invoices, credit notes, receipts, debit notes
     * (`?status=open|paid|void&kind=…&module=&skip=&limit=`; `kind` repeats).
     */
    INVOICES: "/customer/billing/invoices",
    /** One document by id. */
    INVOICE: (id: string) => `/customer/billing/invoices/${id}`,
    /** POST `{ servers: [{ role, region, plan_id }], lane_id? }` prices the order
     *  server-side (capacity, monthly total, prorated amount, shortfall). The
     *  old GET `?prices=` still works but is superseded. */
    ORDER_QUOTE: "/customer/billing/order-quote",
    /** Any document's PDF (`?inline=true` to view rather than download). */
    INVOICE_PDF: (id: string) => `/customer/billing/invoices/${id}/pdf`,
    /** Pay an open monthly invoice early from the wallet (POST). */
    INVOICE_PAY: (id: string) => `/customer/billing/invoices/${id}/pay`,
    /** What paying early would cost now, and whether the wallet covers it. */
    INVOICE_PAY_QUOTE: (id: string) => `/customer/billing/invoices/${id}/pay-quote`,
  },

  /** Regions available when creating a lane. */
  CATALOG: "/customer/catalog",

  /** Public plans (capacity + price only); filter with `?role=`. */
  PLANS: "/customer/plans",

  LANES: {
    LIST: "/customer/lanes",
    CREATE: "/customer/lanes",
    /** One-call snapshot for the whole Lanes page (summary + cards). */
    OVERVIEW_ALL: "/customer/lanes/overview",
    DETAIL: (id: string) => `/customer/lanes/${id}`,
    /** Cancel the whole lane (`?when=now|period_end`, 202 `{ detail }`). */
    DELETE: (id: string) => `/customer/lanes/${id}`,
    /** What cancelling costs / allows (`?node_id=` repeatable, or the lane). */
    CANCEL_OPTIONS: (id: string) => `/customer/lanes/${id}/cancel-options`,
    /** Undo a scheduled lane cancellation (POST). */
    KEEP: (id: string) => `/customer/lanes/${id}/keep`,
    /** SSE live-progress stream (use with `apiUrl()` + EventSource). */
    EVENTS: (id: string) => `/customer/lanes/${id}/events`,
    /** SSE live server metrics (calls/CPS + health) for an active lane. */
    METRICS: (id: string) => `/customer/lanes/${id}/metrics`,
    /** REST snapshot of active calls split per dialer + carrier IP. */
    DIVISION: (id: string) => `/customer/lanes/${id}/division`,
    /** One-call dashboard snapshot for the lane page. */
    OVERVIEW: (id: string) => `/customer/lanes/${id}/overview`,
    /** Paginated friendly event feed (`?limit=&before=`). */
    ACTIVITY: (id: string) => `/customer/lanes/${id}/activity`,
    /** Usage time-series (`?range=24h|7d`). */
    USAGE: (id: string) => `/customer/lanes/${id}/usage`,
    /** History by dialer or carrier (`?kind=dialer|carrier&range=24h|7d|30d`). */
    TRAFFIC_SPLIT: (id: string) => `/customer/lanes/${id}/traffic/split`,
    /** Scale media capacity: add a media server (POST). */
    SERVERS: (id: string) => `/customer/lanes/${id}/servers`,
    /** Cancel a media server (DELETE, `?when=now|period_end`). */
    SERVER: (id: string, nodeId: string) =>
      `/customer/lanes/${id}/servers/${nodeId}`,
    /** Undo a scheduled server cancellation (POST). */
    SERVER_KEEP: (id: string, nodeId: string) =>
      `/customer/lanes/${id}/servers/${nodeId}/keep`,
    /** Stop a media server — keeps billing, drops calls (POST). */
    SERVER_STOP: (id: string, nodeId: string) =>
      `/customer/lanes/${id}/servers/${nodeId}/stop`,
    /** Start a stopped media server (POST). */
    SERVER_START: (id: string, nodeId: string) =>
      `/customer/lanes/${id}/servers/${nodeId}/start`,
    /** Issue a fresh public IP for a media server (POST). Charged the reissue fee (see `BILLING.REISSUE_FEE?node_id=`); refunded if the rebuild fails. */
    SERVER_REISSUE_IP: (id: string, nodeId: string) =>
      `/customer/lanes/${id}/servers/${nodeId}/reissue-ip`,
    /** Replace the whole dialer list (each names its carrier group by id). */
    DIALER_IPS: (id: string) => `/customer/lanes/${id}/dialer-ips`,
    /** Carrier groups: list-scoped create. */
    CARRIER_GROUPS: (id: string) => `/customer/lanes/${id}/carrier-groups`,
    /** A single carrier group: rename / replace carriers / delete. */
    CARRIER_GROUP: (id: string, groupId: string) =>
      `/customer/lanes/${id}/carrier-groups/${groupId}`,
  },
} as const
